import React, { useState, useEffect } from 'react';
import { logger, LogCategory } from '../../utils/logger';
import { useRedirect, useDataProvider } from 'react-admin';
import {
  MessageSquare,
  Users,
  Calendar,
  Clock,
  Activity,
  Zap,
  RefreshCw,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../ui/simple-chart';
import { StatCard } from '../ui/stat-card';
import { toastError } from '../../lib/toast';
import { cn } from '../../lib/utils';
import { CustomDataProvider } from '../../types/dataProvider';

/* ============================================================
   Chat monitoring — Latio design system (derived). Realtime activity board.
   ============================================================ */

const INDIGO = '#4361AD';

interface ChatStats {
  total_messages: number;
  messages_today: number;
  messages_this_week: number;
  personal_session_messages: number;
  group_class_messages: number;
  text_messages: number;
  timeline_event_messages: number;
  system_messages: number;
  active_sessions: number;
  most_active_sessions: Array<{ session_id: number; session_title: string; message_count: number }>;
  most_active_users: Array<{ user_id: number; username: string; email: string; message_count: number }>;
}

interface RealtimeMonitoring {
  active_sessions_with_chat: Array<{ session_id: number; session_title: string; recent_message_count: number; unique_users: number; last_activity: string }>;
  recent_messages: Array<{ id: number; content: string; message_type: string; created_at: string; user: { username: string; email: string }; session: { id: number; title: string } }>;
  messages_last_hour: number;
  unique_users_chatting: number;
  average_messages_per_session: number;
  personal_sessions_active: number;
  group_classes_active: number;
}

function DashCard({ title, icon: Icon, children, className }: { title: string; icon?: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      <h3 className="mb-4 flex items-center gap-2 text-[15px] font-bold text-ink">
        {Icon && <Icon className="size-4 text-ink-2" />}
        {title}
      </h3>
      {children}
    </div>
  );
}

const ActiveSessions: React.FC<{ sessions: RealtimeMonitoring['active_sessions_with_chat'] }> = ({ sessions }) => {
  const redirect = useRedirect();
  return (
    <DashCard title="Active chat sessions" icon={Activity}>
      {sessions.length > 0 ? (
        <div className="flex flex-col">
          {sessions.slice(0, 8).map((s, i) => (
            <div
              key={s.session_id}
              onClick={() => redirect(`/session-chats/${s.session_id}/show`)}
              className={cn('flex cursor-pointer items-center justify-between gap-3 py-3', i > 0 && 'border-t border-hairline')}
            >
              <div className="flex items-center gap-3">
                <span className="flex size-9 items-center justify-center rounded-latio-sm bg-indigo-tint text-indigo">
                  <Calendar className="size-4" />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-ink">{s.session_title || 'Unknown session'}</div>
                  <div className="truncate text-[12.5px] text-ink-3">
                    {s.unique_users || 0} users · Last: {s.last_activity ? new Date(s.last_activity).toLocaleTimeString() : 'Unknown'}
                  </div>
                </div>
              </div>
              <span className="shrink-0 rounded-pill bg-surface-2 px-2 py-0.5 text-[12px] font-semibold text-ink-2">{s.recent_message_count || 0} msgs</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-10 text-center text-ink-3">
          <MessageSquare className="mx-auto mb-2 size-10 text-hairline" />
          <p className="text-sm">No active chats at the moment</p>
        </div>
      )}
    </DashCard>
  );
};

const RecentMessagesFeed: React.FC<{ messages: RealtimeMonitoring['recent_messages'] }> = ({ messages }) => {
  const redirect = useRedirect();
  const truncate = (c: string, n = 80) => (c.length <= n ? c : c.slice(0, n) + '…');
  return (
    <DashCard title="Live message feed" icon={Zap}>
      {messages.length > 0 ? (
        <div className="max-h-96 space-y-1 overflow-y-auto">
          {messages.map((m) => (
            <div
              key={m.id}
              onClick={() => m.session?.id && redirect(`/session-chats/${m.session.id}/show`)}
              className="flex cursor-pointer items-start gap-3 rounded-input p-2 hover:bg-surface-2"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-pill bg-indigo-tint text-indigo">
                <MessageSquare className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-ink">{m.user?.username || 'Unknown user'}</span>
                  <span className="shrink-0 text-[12px] text-ink-3">{new Date(m.created_at).toLocaleTimeString()}</span>
                </div>
                <p className="text-sm text-ink-2">{truncate(m.content || '')}</p>
                <p className="text-[12px] text-ink-3">in {m.session?.title || 'Unknown session'}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-10 text-center text-ink-3">
          <Clock className="mx-auto mb-2 size-10 text-hairline" />
          <p className="text-sm">No recent messages</p>
        </div>
      )}
    </DashCard>
  );
};

const MessagesBarChart: React.FC<{ title: string; data: { type: string; count: number }[] }> = ({ title, data }) => {
  const config = { count: { label: 'Messages', color: INDIGO } } satisfies ChartConfig;
  return (
    <DashCard title={title}>
      <ChartContainer config={config} className="h-[280px] w-full">
        <BarChart data={data} margin={{ left: -16, right: 8, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" vertical={false} />
          <XAxis dataKey="type" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#757575' }} />
          <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#757575' }} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Bar dataKey="count" fill={INDIGO} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ChartContainer>
    </DashCard>
  );
};

const TopActivity: React.FC<{ users: ChatStats['most_active_users']; sessions: ChatStats['most_active_sessions'] }> = ({ users, sessions }) => {
  const redirect = useRedirect();
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <DashCard title="Most active users" icon={Users}>
        <div className="flex flex-col">
          {users.slice(0, 5).map((u, i) => (
            <div
              key={u.user_id}
              onClick={() => redirect(`/users/${u.user_id}/show`)}
              className={cn('flex cursor-pointer items-center justify-between gap-3 py-2.5', i > 0 && 'border-t border-hairline')}
            >
              <div className="flex items-center gap-3">
                <span className="flex size-6 items-center justify-center rounded-pill bg-indigo-tint text-[12px] font-semibold text-indigo">{i + 1}</span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">{u.username || 'Unknown user'}</div>
                  <div className="truncate text-[12px] text-ink-3">{u.email || 'No email'}</div>
                </div>
              </div>
              <span className="rounded-pill bg-surface-2 px-2 py-0.5 text-[12px] font-semibold text-ink-2">{u.message_count || 0}</span>
            </div>
          ))}
        </div>
      </DashCard>

      <DashCard title="Most active sessions" icon={Calendar}>
        <div className="flex flex-col">
          {sessions.slice(0, 5).map((s, i) => (
            <div
              key={s.session_id}
              onClick={() => redirect(`/session-chats/${s.session_id}/show`)}
              className={cn('flex cursor-pointer items-center justify-between gap-3 py-2.5', i > 0 && 'border-t border-hairline')}
            >
              <div className="flex items-center gap-3">
                <span className="flex size-6 items-center justify-center rounded-pill bg-indigo-tint text-[12px] font-semibold text-indigo">{i + 1}</span>
                <div className="truncate text-sm font-medium text-ink">{s.session_title || 'Unknown session'}</div>
              </div>
              <span className="rounded-pill bg-surface-2 px-2 py-0.5 text-[12px] font-semibold text-ink-2">{s.message_count || 0}</span>
            </div>
          ))}
        </div>
      </DashCard>
    </div>
  );
};

export const ChatMonitoring: React.FC = () => {
  const [chatStats, setChatStats] = useState<ChatStats | null>(null);
  const [realtimeData, setRealtimeData] = useState<RealtimeMonitoring | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const dataProvider = useDataProvider<CustomDataProvider>();

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const [statsResult, monitoringResult] = await Promise.allSettled([dataProvider.getChatStats(), dataProvider.getChatMonitoring()]);
      if (statsResult.status === 'fulfilled') setChatStats(statsResult.value.data);
      if (monitoringResult.status === 'fulfilled') setRealtimeData(monitoringResult.value.data);
    } catch (error) {
      logger.error(LogCategory.API, 'Error fetching chat monitoring data', { error }, 'ChatMonitoring');
      toastError('Failed to fetch chat monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !chatStats || !realtimeData) {
    return (
      <div className="flex h-64 items-center justify-center p-6">
        <div className="text-center text-ink-2">
          <RefreshCw className="mx-auto mb-2 size-8 animate-spin text-ink-3" />
          <p className="text-sm">Loading chat monitoring data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
            Chat monitoring
          </h1>
          <p className="mt-1 text-sm text-ink-2">Real-time chat activity and statistics</p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={refreshing}
          className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
        >
          <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard primary label="Total messages" value={(chatStats.total_messages || 0).toLocaleString('de-DE')} delta={chatStats.messages_today ? { value: `${chatStats.messages_today} today`, up: true } : undefined} />
        <StatCard label="Active sessions" value={chatStats.active_sessions || 0} />
        <StatCard label="Users chatting" value={realtimeData.unique_users_chatting || 0} />
        <StatCard label="Messages / hour" value={realtimeData.messages_last_hour || 0} />
        <StatCard label="Avg msgs / session" value={realtimeData.average_messages_per_session ? realtimeData.average_messages_per_session.toFixed(1) : '0.0'} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ActiveSessions sessions={realtimeData.active_sessions_with_chat || []} />
        <RecentMessagesFeed messages={realtimeData.recent_messages || []} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <MessagesBarChart
          title="Message types distribution"
          data={[
            { type: 'Text', count: chatStats.text_messages || 0 },
            { type: 'Timeline', count: chatStats.timeline_event_messages || 0 },
            { type: 'System', count: chatStats.system_messages || 0 },
          ]}
        />
        <MessagesBarChart
          title="Messages by session type"
          data={[
            { type: 'Personal', count: chatStats.personal_session_messages || 0 },
            { type: 'Group class', count: chatStats.group_class_messages || 0 },
          ]}
        />
      </div>

      <TopActivity users={chatStats.most_active_users || []} sessions={chatStats.most_active_sessions || []} />
    </div>
  );
};
